import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ARIA_ESCALATION_API_STATUS } from '../ariaHandsoffConstants';

const AriaBotMark = () => (
  <Box
    component="svg"
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block', flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="9" stroke="#9f6b66" strokeWidth="1.7" />
    <circle cx="12" cy="12" r="2.4" fill="#9f6b66" />
  </Box>
);

/**
 * Strip above the composer while an admin owns the thread (in_progress).
 */
export const AriaTakenOverBanner = ({ memberName, onHandBack, handingBack = false }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.5,
      px: 2,
      pt: 1.25,
      pb: 0.25,
      borderTop: '1px solid',
      borderTopColor: 'divider',
      bgcolor: 'background.paper',
      flexShrink: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#166534',
        lineHeight: 1.35,
        minWidth: 0,
      }}
    >
      ● You&apos;ve taken over — you&apos;re now chatting with {memberName}
    </Typography>
    <Button
      variant="text"
      onClick={onHandBack}
      disabled={handingBack}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.75rem',
        color: 'error.dark',
        px: 0.5,
        py: 0.25,
        minWidth: 0,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: 'transparent', color: 'error.main' },
        '&.Mui-disabled': { color: 'error.light' },
      }}
    >
      {handingBack ? 'Returning…' : 'Return to Aria'}
    </Button>
  </Box>
);

/**
 * Bottom bar for Aria Handsoff room.
 * open → Aria holding + Take over
 * in_progress → taken-over banner lives above ChatPanel composer (AriaTakenOverBanner)
 * resolved → read-only notice
 */
const AriaChatFooter = ({ apiStatus, onTakeHandover, takingOver = false }) => {
  const status = String(apiStatus ?? '').toLowerCase();

  if (status === ARIA_ESCALATION_API_STATUS.IN_PROGRESS) {
    return null;
  }

  if (status === ARIA_ESCALATION_API_STATUS.RESOLVED) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderTopColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1.125,
            borderRadius: '12px',
            bgcolor: 'action.hover',
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>
            This conversation is resolved.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Default / open — Aria is still holding
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'stretch', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.25,
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderTopColor: 'divider',
        bgcolor: 'background.paper',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.125,
          borderRadius: '12px',
          bgcolor: 'action.hover',
        }}
      >
        <AriaBotMark />
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'text.secondary',
            lineHeight: 1.35,
          }}
        >
          Aria is holding this conversation until a person steps in
        </Typography>
      </Box>

      <Button
        variant="contained"
        onClick={onTakeHandover}
        disabled={takingOver}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.8125rem',
          borderRadius: '999px',
          px: 2.25,
          py: 1,
          flexShrink: 0,
          bgcolor: 'success.dark',
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: 'success.main', boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: 'success.light',
            color: 'success.dark',
          },
        }}
      >
        {takingOver ? 'Taking over…' : 'Take over from Aria'}
      </Button>
    </Box>
  );
};

export default AriaChatFooter;
